/**
 * Voice recorder composable wrapping MediaRecorder.
 *
 * - Records `audio/webm;codecs=opus` by default (falls back to whatever the
 *   browser supports for the MediaRecorder API).
 * - Tracks elapsed time and a coarse audio level (0..1) for a VU-style meter.
 * - Automatically stops at `maxDurationSeconds` if provided.
 * - `stop()` resolves to a `File` containing the recorded audio.
 */
import type { MaybeRefOrGetter } from "vue";

export interface UseVoiceRecorderOptions {
  /** Maximum duration in seconds. 0 / undefined means no limit. */
  maxDurationSeconds?: MaybeRefOrGetter<number | null | undefined>;
  /** Preferred mime type. Defaults to "audio/webm;codecs=opus". */
  mimeType?: string;
  /** Called when recording auto-stops due to reaching `maxDurationSeconds`. */
  onMaxDurationReached?: () => void;
}

function pickSupportedMimeType(preferred?: string): string {
  const candidates = [
    preferred,
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ].filter(Boolean) as string[];

  if (typeof MediaRecorder === "undefined") {
    return candidates[0] ?? "audio/webm";
  }

  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate)) {
      return candidate;
    }
  }
  return candidates[0] ?? "audio/webm";
}

function mimeTypeToExtension(mimeType: string): string {
  if (mimeType.startsWith("audio/webm")) return "webm";
  if (mimeType.startsWith("audio/ogg")) return "ogg";
  if (mimeType.startsWith("audio/mp4")) return "m4a";
  if (mimeType.startsWith("audio/mpeg")) return "mp3";
  if (mimeType.startsWith("audio/wav")) return "wav";
  return "webm";
}

export function useVoiceRecorder(options: UseVoiceRecorderOptions = {}) {
  const isSupported =
    typeof window !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    !!navigator?.mediaDevices?.getUserMedia;

  const isRecording = ref(false);
  const isPaused = ref(false);
  const elapsedMs = ref(0);
  const level = ref(0);
  const error = ref<Error | null>(null);

  let mediaRecorder: MediaRecorder | null = null;
  let mediaStream: MediaStream | null = null;
  let audioContext: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let chunks: BlobPart[] = [];
  let startedAt = 0;
  let pausedElapsedMs = 0;
  let timerHandle: ReturnType<typeof setInterval> | null = null;
  let rafHandle: number | null = null;
  let stopResolve: ((file: File) => void) | null = null;
  let stopReject: ((err: unknown) => void) | null = null;
  let activeMimeType = pickSupportedMimeType(options.mimeType);

  function clearTimers() {
    if (timerHandle) {
      clearInterval(timerHandle);
      timerHandle = null;
    }
    if (rafHandle && typeof cancelAnimationFrame !== "undefined") {
      cancelAnimationFrame(rafHandle);
      rafHandle = null;
    }
  }

  function tearDownAudio() {
    clearTimers();
    if (source) {
      try {
        source.disconnect();
      } catch {
        /* ignore */
      }
      source = null;
    }
    if (analyser) {
      try {
        analyser.disconnect();
      } catch {
        /* ignore */
      }
      analyser = null;
    }
    if (audioContext) {
      audioContext.close().catch(() => {
        /* ignore */
      });
      audioContext = null;
    }
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      mediaStream = null;
    }
  }

  function resolveMaxDurationMs(): number {
    const raw = toValue(options.maxDurationSeconds);
    if (!raw || raw <= 0) return 0;
    return Math.floor(raw * 1000);
  }

  function startMonitoring() {
    if (!analyser) return;
    const buffer = new Uint8Array(analyser.fftSize);

    const tick = () => {
      if (!analyser) return;
      analyser.getByteTimeDomainData(buffer);
      let peak = 0;
      for (let i = 0; i < buffer.length; i += 1) {
        const value = Math.abs(buffer[i] - 128) / 128;
        if (value > peak) peak = value;
      }
      level.value = peak;
      rafHandle = requestAnimationFrame(tick);
    };
    rafHandle = requestAnimationFrame(tick);
  }

  async function start() {
    if (!isSupported) {
      const err = new Error("Audio recording is not supported in this browser.");
      error.value = err;
      throw err;
    }
    if (isRecording.value) return;

    error.value = null;
    chunks = [];
    elapsedMs.value = 0;
    pausedElapsedMs = 0;
    level.value = 0;
    activeMimeType = pickSupportedMimeType(options.mimeType);

    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      error.value = err as Error;
      throw err;
    }

    try {
      const AC: typeof AudioContext =
        window.AudioContext ?? (window as any).webkitAudioContext;
      if (AC) {
        audioContext = new AC();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024;
        source = audioContext.createMediaStreamSource(mediaStream);
        source.connect(analyser);
        startMonitoring();
      }
    } catch {
      /* level meter is best-effort */
    }

    try {
      mediaRecorder = new MediaRecorder(mediaStream, { mimeType: activeMimeType });
    } catch {
      mediaRecorder = new MediaRecorder(mediaStream);
      activeMimeType = mediaRecorder.mimeType || activeMimeType;
    }

    mediaRecorder.addEventListener("dataavailable", (event) => {
      if (event.data && event.data.size > 0) {
        chunks.push(event.data);
      }
    });

    mediaRecorder.addEventListener("error", (event) => {
      const err = (event as any).error ?? new Error("Recording failed.");
      error.value = err;
      if (stopReject) {
        stopReject(err);
        stopReject = null;
        stopResolve = null;
      }
      tearDownAudio();
      isRecording.value = false;
      isPaused.value = false;
    });

    mediaRecorder.addEventListener("stop", () => {
      const finalMime = activeMimeType || "audio/webm";
      const blob = new Blob(chunks, { type: finalMime });
      const ext = mimeTypeToExtension(finalMime);
      const file = new File([blob], `voice-note.${ext}`, { type: finalMime });
      tearDownAudio();
      isRecording.value = false;
      isPaused.value = false;
      if (stopResolve) {
        stopResolve(file);
        stopResolve = null;
        stopReject = null;
      }
    });

    startedAt = Date.now();
    isRecording.value = true;
    isPaused.value = false;
    mediaRecorder.start();

    timerHandle = setInterval(() => {
      if (!isRecording.value || isPaused.value) return;
      elapsedMs.value = pausedElapsedMs + (Date.now() - startedAt);
      const maxMs = resolveMaxDurationMs();
      if (maxMs > 0 && elapsedMs.value >= maxMs) {
        options.onMaxDurationReached?.();
        void stop();
      }
    }, 100);
  }

  function pause() {
    if (!mediaRecorder || !isRecording.value || isPaused.value) return;
    try {
      mediaRecorder.pause();
      pausedElapsedMs += Date.now() - startedAt;
      isPaused.value = true;
    } catch {
      /* ignore */
    }
  }

  function resume() {
    if (!mediaRecorder || !isRecording.value || !isPaused.value) return;
    try {
      mediaRecorder.resume();
      startedAt = Date.now();
      isPaused.value = false;
    } catch {
      /* ignore */
    }
  }

  function stop(): Promise<File> {
    if (!mediaRecorder || !isRecording.value) {
      return Promise.reject(new Error("Not recording."));
    }
    return new Promise<File>((resolve, reject) => {
      stopResolve = resolve;
      stopReject = reject;
      try {
        if (mediaRecorder?.state !== "inactive") {
          mediaRecorder?.stop();
        }
      } catch (err) {
        stopResolve = null;
        stopReject = null;
        tearDownAudio();
        isRecording.value = false;
        isPaused.value = false;
        reject(err);
      }
    });
  }

  function cancel() {
    if (!mediaRecorder) {
      tearDownAudio();
      return;
    }
    try {
      if (mediaRecorder.state !== "inactive") {
        chunks = [];
        mediaRecorder.stop();
      }
    } catch {
      /* ignore */
    }
    stopResolve = null;
    stopReject = null;
    isRecording.value = false;
    isPaused.value = false;
    tearDownAudio();
  }

  onScopeDispose(() => {
    cancel();
  });

  return {
    isSupported,
    isRecording,
    isPaused,
    elapsedMs,
    level,
    error,
    start,
    pause,
    resume,
    stop,
    cancel,
    mimeType: () => activeMimeType,
    extension: () => mimeTypeToExtension(activeMimeType),
  };
}
